import React, { useEffect, useState, useMemo } from 'react';
import { Button, Flex, message, Space, Spin, Table, Tooltip, TreeSelect, Typography } from "antd";
import Container from "../../components/UI/Container/Container";
import Block from "../../components/UI/Block/Block";
import { API } from "../../http";
import { NEW_ENDPOINTS } from "../../http/endpoints";
import moment from "moment";
import { ReloadOutlined } from "@ant-design/icons";

const getTreeData = (operations) => {
  const tree = {};

  operations.forEach(({ AccountTo }) => {
    if (!AccountTo) return;

    const parts = AccountTo.split('.');
    let currentLevel = tree;

    parts.forEach((part, index) => {
      const fullValue = parts.slice(0, index + 1).join('.');
      if (!currentLevel[part]) {
        currentLevel[part] = {
          value: fullValue,
          label: fullValue,
          children: {},
        };
      }
      currentLevel = currentLevel[part].children;
    });
  });

  const convertTreeToArray = (tree) => {
    return Object.keys(tree).map((key) => {
      const node = tree[key];
      return {
        title: node.label,
        value: node.value,
        children: convertTreeToArray(node.children),
      };
    });
  };

  return convertTreeToArray(tree);
};

const Ledger = () => {
  const [loading, setLoading] = useState(false);
  const [operations, setOperations] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [sorterInfo, setSorterInfo] = useState({ field: 'Date', order: 'descend' });
  const [treeOpen, setTreeOpen] = useState(true);

  const fetchOperations = async () => {
    setLoading(true);
    try {
      const { data } = await API.post(NEW_ENDPOINTS.ADMIN.TRANSACTIONS.LEDGER, {
        start: "2024-01-01",
        end: "2025-01-01",
      });
      setOperations(data);
    } catch (e) {
      message.error('Error fetching Ledger History');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOperations();
  }, []);

  const treeData = useMemo(() => getTreeData(operations), [operations]);

  const handleAccountSelect = (value) => {
    setSelectedAccount(value);
    setTreeOpen(false);
  };

  const filteredOperations = useMemo(() => {
    if (!selectedAccount) return [];
    return operations.filter(operation => operation.AccountTo.startsWith(selectedAccount));
  }, [selectedAccount, operations]);

  const columns = [
    { title: 'Account To', dataIndex: 'AccountTo', key: 'AccountTo' },
    { title: 'Account From', dataIndex: 'AccountFrom', key: 'AccountFrom' },
    {
      title: 'Amount',
      dataIndex: 'Amount',
      key: 'Amount',
      align: 'center',
      render: (value) => <Typography>{value} EUR</Typography>,
      sorter: (a, b) => a.Amount - b.Amount,
      sortOrder: sorterInfo.field === 'Amount' ? sorterInfo.order : null,
    },
    {
      title: 'Date',
      dataIndex: 'Date',
      key: 'Date',
      align: 'right',
      render: (text) => moment(text).format('YYYY-MM-DD HH:mm:ss'),
      sorter: (a, b) => moment(a.Date).diff(moment(b.Date)),
      defaultSortOrder: 'descend',
      sortOrder: sorterInfo.field === 'Date' ? sorterInfo.order : null,
    },
  ];

  const handleTableChange = (pagination, filters, sorter) => {
    setSorterInfo(sorter);
  };

  return (
    <Container>
      <Spin tip="Loading..." spinning={loading}>
        <Block>
          <Typography.Title level={2}>Ledger Operations</Typography.Title>

          <Flex style={{ marginBottom: 20 }} justify="space-between" gap={30} className="w100" align="baseline">
            <TreeSelect
              style={{ width: '100%' }}
              size="large"
              value={selectedAccount}
              dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
              treeData={treeData}
              placeholder="Select Account"
              onChange={handleAccountSelect}
              treeDefaultExpandAll
              open={treeOpen}
              onDropdownVisibleChange={(open) => setTreeOpen(open)}
            />
            <Space>
              <Tooltip title="Reload">
                <Button icon={<ReloadOutlined />} onClick={fetchOperations} shape="round" type="primary" />
              </Tooltip>
            </Space>
          </Flex>

          {!selectedAccount ? (
            <Typography.Text type="secondary">Please select an account to view ledger operations.</Typography.Text>
          ) : (
            <Table
              rowKey={(record) => `${record.AccountFrom}-${record.AccountTo}-${record.Date}`}
              dataSource={filteredOperations}
              columns={columns}
              onChange={handleTableChange}
              showSorterTooltip={false}
              pagination={{ position: ['none', 'bottomCenter'], pageSize: 20, hideOnSinglePage: true }}
              sortDirections={['descend', 'ascend']}
            />
          )}
        </Block>
      </Spin>
    </Container>
  );
};

export default Ledger;
